import React from 'react';
import { PortfolioData } from 'src/app/portfolio/state/api/portfolioQuery';
import { useIsGridBackedSpecies } from 'src/app/portfolio/state/portfolioSpeciesSource';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import { SortOrder } from 'src/shared/types';
import IUCNLogo from 'src/assets/images/dashboard/iucn.png';
import IucnSpeciesChart from 'src/shared/components/iucn-species-chart/IucnSpeciesChart';
import PortfolioExploreButton from './PortfolioExploreButton';
import SummaryCard from './SummaryCard';

const IucnSummaryCard = ({ portfolioData }: { portfolioData: PortfolioData }) => {
    const navigate = useAppNavigation();
    // Explore has to land on the species page reading the same source as this card:
    // sending grid figures to the projection's page answers the same question twice
    // with different numbers.
    const isGridBacked = useIsGridBackedSpecies();

    const redListSpecies = portfolioData.summary.predictedSpeciesSummary.redListSpecies;
    if (!redListSpecies) {
        return null;
    }

    const iucnRedListSites = portfolioData.summary.predictedSpeciesSummary.redListSpecies.siteCount;
    const percentageIucnRedListSites = (
        (portfolioData.summary.predictedSpeciesSummary.redListSpecies.siteCount / portfolioData.summary.numberSites) *
        100
    ).toFixed(0);

    return (
        <SummaryCard
            dataTestId='portfolio-iucn-red-list-card'
            containerClasses='gap-3 p-5'
            cardIcon={<img src={IUCNLogo} alt='' className='w-[26px] h-[28x]' />}
            cardTitle='IUCN Red List Species'
            cardSubtitle='Possible IUCN Red List Species overlap across portfolio'
            cardBody={
                <div className='flex gap-4'>
                    <div className='flex flex-col'>
                        <div className='flex gap-2 items-end'>
                            <span className='text-[42px] text-grey-80'>{iucnRedListSites}</span>
                            <span className='text-xl text-grey-80 pb-3'>({percentageIucnRedListSites}%)</span>
                        </div>
                        <div className='text-sm text-grey-80'>
                            Sites with possible
                            <br />
                            IUCN Red List species
                        </div>
                    </div>
                    <div className='bg-grey-20 w-[1px]'>&nbsp;</div>
                    <div className='flex flex-col'>
                        <div className='flex gap-2 items-end'>
                            <span className='text-[42px] text-grey-80'>{redListSpecies.count}</span>
                        </div>
                        <div className='text-sm text-grey-80'>Total red list species</div>
                    </div>
                    <IucnSpeciesChart className='flex' chartData={redListSpecies.chartData} />
                </div>
            }
            action={
                <PortfolioExploreButton
                    sectionName='iucn-red-list-species'
                    onClick={() =>
                        isGridBacked
                            ? navigate.toNextPortfolioSpeciesRiskIndicators({ tab: 'iucn-red-list-species' })
                            : navigate.toPortfolioSpeciesRiskIndicators({ tab: 'iucn-red-list-species', sortDirection: SortOrder.DESC })
                    }
                />
            }
            frameworks={redListSpecies.frameworks}
        />
    );
};

export default IucnSummaryCard;
